import { useContext, useEffect } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import dateFormat from "dateformat";
import cx from "classnames";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { fetchDealRoomHistory, fetchDealRoomSignals } from "src/redux/actions/dealRoom";
import InfiniteScroll from "src/components/infiniteScroll";
import { DealRoomContext } from "../../context";

import EmptyGraph from 'src/legacy/images/emptyGraph.png'
import styles from "./styles.module.scss";

const PlaceholderCard = () => {
  return (
    <div className={cx(styles.placeholderCard)}>
      <div className={cx(styles.content, styles.block)}></div>
    </div>
  )
}

const TimeLineChartCustomToolTip = ({ active, payload }) => {
  if (active && !_.isEmpty(payload)) {
    let item = _.get(payload[0], 'payload', null);

    let confidence = _.get(item, 'aiConfidence', 0);
    confidence = confidence.toFixed(2);

    return (
      <div className={styles.tooltipBox}>
        <p className={styles.tootltipTitle}>{_.get(item, 'stageValue')} </p>
        <div className={styles.tooltipBody}>
          <div className={styles.probabilityContainer}>
            <p className={styles.tooltipValue}>{_.round(confidence, 0)}%</p>
            <p className={styles.content}>{"Win Probability"}</p>
          </div>
        </div>
        <p className={styles.tooltipDate}>{`${dateFormat(_.get(item, "createdDate"), "dd mmm yyyy")}`}</p>
      </div>
    );
  }

  return <></>
}

const Insights = () => {
  const dispatch = useDispatch();
  const { id: dealRoomId, dealId } = useContext(DealRoomContext);
  
  const graphData = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom['${dealRoomId}'].dealInsights.history`, null);
  });
  const primaryColor = useSelector((state: any) => {
    return _.get(state, 'account.theme.primaryColor', '#E50C58');
  });
  
  const graphArr = _.get(graphData, 'items', []);
  const loading = _.get(graphData, 'loading', false);
  const loaded = _.get(graphData, 'loaded', false);
  
  useEffect(() => {
    if (!loading && !loaded) {
      dispatch(fetchDealRoomHistory({
        dealRoomId: dealRoomId ?? '',
        dealId: dealId ?? '',
        page: 0
      }));
    }
  }, [loading, loaded]);

  return (
    <div className={styles.analyticsContainer}>
      {loading && (
        <div className={styles.loadingMessageContainer}>
          <p>Loading...</p>
        </div>
      )}

      {_.size(graphArr) > 0 && !loading && (
        <div className={styles.graphContainer}>
          <ResponsiveContainer>
            <AreaChart data={graphArr}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={primaryColor} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis axisLine={false} dataKey="createdDateStr" dy={15} />
              <YAxis axisLine={false} dataKey="aiConfidence" dx={-15} />
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <Tooltip
                wrapperStyle={{ outlineColor: primaryColor }}
                content={<TimeLineChartCustomToolTip payload={graphArr} active={true} />}
              />
              <Area type="monotone" dataKey="aiConfidence" strokeWidth={3} stroke={primaryColor} fillOpacity={1} fill="url(#colorUv)" />
            </AreaChart>

          </ResponsiveContainer>
        </div>
      )}

      {_.size(graphArr) === 0 && !loading && <div className={styles.emptyGraphContainer}><img src={EmptyGraph} alt="empty graph" /></div>}
    </div>
  )
}

const SignalCards = () => {
  const dispatch = useDispatch();
  const { id: dealRoomId, dealId } = useContext(DealRoomContext);

  const signalsData = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom['${dealRoomId}'].dealInsights.signals`, null);
  });

  const signals = _.get(signalsData, 'items', []);
  const currentPage = _.get(signalsData, 'pagination.currentPage', -1);
  const totalPages = _.get(signalsData, 'pagination.totalPages', 0);
  const hasMoreItems = _.get(signalsData, 'pagination.hasMoreItems', false);
  const loading = _.get(signalsData, 'loading', false);
  const loaded = _.get(signalsData, 'loaded', false);

  useEffect(() => {
    if (!loading && !loaded) {
      dispatch(fetchDealRoomSignals({
        dealRoomId: dealRoomId ?? '',
        dealId : dealId ?? '',
        page: 0,
      }));
    }
  }, [loading, loaded]);

  const loadMoreCards = () => {
    const nextPage = currentPage + 1;
    if (loading || nextPage >= totalPages) {
      return;
    }

    dispatch(fetchDealRoomSignals({
      dealRoomId: dealRoomId ?? '',
      dealId: dealId ?? '',
      page: nextPage
    }));
  }

  return (
    <div className={styles.signalsContainer}>
      <div className={styles.scrollContainer}>
        <InfiniteScroll
          className={styles.signalCards}
          loadMore={loadMoreCards}
          loader={() => {
            if (_.size(signals) > 0) {
              return <PlaceholderCard />
            }
            return (
              <>
                <PlaceholderCard />
                <PlaceholderCard />
                <PlaceholderCard />
              </>
            );
          }}
          hasMore={hasMoreItems}
          loading={loading}
        >
          {
            _.size(signals) > 0 && _.map(signals, (signal: any, index) => {
              return (
                <div key={index} className={styles.signalCard}>
                  <div className={styles.card}>
                    <h3>{index + 1 < 10 ? `0${index + 1}` : index + 1} </h3>
                    <div className={styles.signalContent}>
                      <p className={styles.signalBody}>
                        {_.get(signal, "subject", 'N/A')}
                      </p>
                      <p className={styles.signalValue}>
                        {_.get(signal, "message", 'N/A')}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })
          }
        </InfiniteScroll>
      </div>
    </div>
  )
}

const DealInsights = () => {
  return (
    <div className={styles.container}>
      <div className={styles.cardContainer}>
        <Insights />
        <SignalCards />
      </div>
    </div>
  )
}

export default DealInsights;